import { makeAsync } from '@client/new/helpers/async';
import { omitObject, pickObject } from '@client/new/helpers/general';
import { Actions, Async, toAsyncFetched } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useBrowserHistorySync } from '@client/new/hooks/useBrowserHistorySync';
import { emitGAEvent } from '@client/new/services/ga4';
import { track } from '@services/tracking';
import { navigate, replacePath } from '@client/routes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { navigateWithAuthTransition } from '../auth.navigation';
import { useAuthModel } from '../model/auth.model';
import { isRecoverableCompleteRegistrationError } from '../model/auth.registration-error.model';
import type {
  FlowSessionSource,
  PersistDraftFieldPayload,
  RegistrationDraft,
  RegistrationDraftKey,
} from '../model/auth.types';
import { useSsoSessionStore } from '@client/new/stores/sso-session.store';
import { ssoRegister } from '@client/new/api/sso.api';
import { useUserStore } from '@client/new/stores/user.store';
import { useGoogleSso } from './google-sso.hook';
import { useAppleSso } from './apple-sso.hook';
import {
  EXISTING_USER_ERROR_MESSAGE,
  GENERIC_ERROR_MESSAGE,
  INVALID_CODE_ERROR_MESSAGE,
  notifyExistingUserError,
  notifyInvalidCodeError,
  trackContinueAction,
  trackMobilePhoneSubmitted,
  trackSuccessfulLogin,
  trackSignInWithSso,
} from './register.effects';
import {
  canContinueFromStep,
  CONFIRMATION_CODE_LENGTH,
  getPreviousRegisterStep,
  isEmailValid,
  type RegisterStepValidationRules,
} from './register.progress';
import {
  getPathForStep,
  getRegisterStepFields,
  isPersistedRegisterField,
  stepIncludesField,
} from '../model/registration.model';
import {
  resolveRegisterPathSync,
  resolveInitialRegisterState,
} from './register.session';
import { useRegisterSessionLifecycle } from './register.lifecycle';
import type {
  RegisterActionMap,
  RegisterSubmissionPayloadByStage,
  RegisterSubmissionStage,
  RegisterSubmissionState,
  RegisterStepFormState,
  RegisterStep,
  RegisterStepValuesMap,
  RegisterValues,
  RegisterViewModel,
} from './register.types';

export { CONFIRMATION_CODE_LENGTH };

type RegisterAction = Actions<RegisterActionMap>;

const POST_AUTH_REDIRECT_PATH = {
  login: '/home',
  registration: '/onboarding',
} as const;

const isSubmissionPending = (submission: RegisterSubmissionState): boolean =>
  submission.type !== 'idle' && submission.type !== 'error';

const createInitialSubmissionState = (): RegisterSubmissionState => ({
  type: 'idle',
});

const toGenericSubmissionError = <TStage extends RegisterSubmissionStage>(
  stage: TStage,
  payload: RegisterSubmissionPayloadByStage[TStage],
): RegisterSubmissionState =>
  ({
    type: 'error',
    message: GENERIC_ERROR_MESSAGE,
    stage,
    payload,
  } as RegisterSubmissionState);

const toContinueTrackedValue = (
  step: RegisterStep,
  values: RegisterValues,
  validationRules: Pick<
    RegisterStepValidationRules,
    'parseEmail' | 'parseInstagram' | 'parseTiktok' | 'parseYoutubeChannel'
  >,
): string | null => {
  switch (step) {
    case 'email':
      return validationRules.parseEmail(values.email);
    case 'name':
      return `${values.firstName.trim().toLowerCase()}|${values.lastName
        .trim()
        .toLowerCase()}`;
    case 'instagram':
      return [
        validationRules.parseInstagram(values.instagram) ?? '',
        validationRules.parseTiktok(values.tiktok) ?? '',
        validationRules.parseYoutubeChannel(values.youtube) ?? '',
      ].join('|');
    case 'phone':
    case 'code':
      return null;
  }
};

export const useRegisterViewModel = (): Async<RegisterViewModel> => {
  const model = useAuthModel();
  const validationRules = model.queries.identity;
  const clearPersistedSession = useCallback(
    () => model.dispatch('ClearFlowSession'),
    [model],
  );
  const initialState = useMemo(
    () =>
      resolveInitialRegisterState({
        initialDraft: model.queries.loadDraft(),
        validationRules,
        persistedSession: model.queries.readFlowSession(),
        clearPersistedSession,
      }),
    [clearPersistedSession, model, validationRules],
  );

  const autoSubmittedCodeRef = useRef('');
  const ssoTransitionRef = useRef(false);
  const ssoSession = useSsoSessionStore();
  const googleSso = useGoogleSso('signup');
  const appleSso = useAppleSso('signup');

  const ssoPrefilledValues = useMemo((): Partial<
    Pick<RegisterValues, 'email' | 'firstName' | 'lastName'>
  > => {
    if (!ssoSession.idToken) {
      return {};
    }

    let email = '';
    let firstName = ssoSession.firstName ?? '';
    let lastName = ssoSession.lastName ?? '';

    try {
      const payload = JSON.parse(atob(ssoSession.idToken.split('.')[1]));
      email = payload.email ?? '';
      if (!firstName) {
        firstName = payload.given_name ?? '';
      }
      if (!lastName) {
        lastName = payload.family_name ?? '';
      }
    } catch {
      return {};
    }

    return { email, firstName, lastName };
  }, [ssoSession.idToken, ssoSession.firstName, ssoSession.lastName]);

  const ssoInitialStep: RegisterStep | null = (() => {
    if (!ssoPrefilledValues.email) {
      return null;
    }
    if (!ssoPrefilledValues.firstName) {
      return 'name';
    }
    return 'instagram';
  })();

  const [step, setStep] = useState(ssoInitialStep ?? initialState.step);
  const [values, setValues] = useState(() => ({
    ...initialState.values,
    ...ssoPrefilledValues,
  }));
  const [sessionSource, setSessionSource] = useState(
    initialState.sessionSource,
  );
  const [submission, setSubmission] = useState(createInitialSubmissionState);
  const { stopSessionPersistence } = useRegisterSessionLifecycle({
    model,
    sessionSource,
    step,
    email: values.email,
  });

  useEffect(() => {
    if (!ssoInitialStep) {
      if (initialState.redirectPath) {
        replacePath(initialState.redirectPath);
      }
      return;
    }

    ssoTransitionRef.current = true;
    setStep(ssoInitialStep);
    setValues((prev) => ({ ...prev, ...ssoPrefilledValues }));
    replacePath(getPathForStep(ssoInitialStep, 'signup'));
    requestAnimationFrame(() => {
      ssoTransitionRef.current = false;
    });
  }, [initialState.redirectPath, ssoInitialStep, ssoPrefilledValues]);

  const clearPersistedAndLocalFlowState = useCallback((): void => {
    stopSessionPersistence();
    autoSubmittedCodeRef.current = '';
    model.dispatch('ClearFlowSession');
    model.dispatch('ClearDraft');

    setStep('email');
    setValues({
      ...model.queries.loadDraft(),
      code: '',
    });
    setSubmission({ type: 'idle' });
  }, [model, stopSessionPersistence]);

  const syncStepFromPathname = useCallback((): void => {
    if (typeof window === 'undefined') {
      return;
    }

    if (ssoTransitionRef.current) {
      return;
    }

    const pathSync = resolveRegisterPathSync({
      pathname: window.location.pathname,
      currentStep: step,
      currentSource: sessionSource,
      values,
      validationRules,
    });
    if (!pathSync) {
      return;
    }

    if (pathSync.shouldClearCode) {
      setValues((previousValues) => ({
        ...previousValues,
        code: '',
      }));
    }

    setSubmission({ type: 'idle' });
    autoSubmittedCodeRef.current = '';

    setSessionSource(pathSync.nextSource);
    setStep(pathSync.nextStep);

    if (pathSync.redirectPath) {
      replacePath(pathSync.redirectPath);
    }
  }, [sessionSource, step, validationRules, values]);

  useBrowserHistorySync(syncStepFromPathname);

  const persistDraftField = <TKey extends RegistrationDraftKey>(
    key: TKey,
    value: RegistrationDraft[TKey],
  ): void => {
    // The compound { key, value } structurally matches the
    // PersistDraftFieldPayload<TKey> member of the distributed union, but the
    // generic TKey prevents TS from picking that branch on its own. The
    // `unknown` hop makes the unsoundness explicit so review can see it
    // without changing the dispatcher API.
    const payload = { key, value } as unknown as PersistDraftFieldPayload;
    model.dispatch('PersistDraftField', payload);
  };

  const updateValue = <TKey extends keyof RegisterValues>(
    key: TKey,
    value: RegisterValues[TKey],
  ): void => {
    if (submission.type === 'error') {
      setSubmission({ type: 'idle' });
    }

    setValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));

    if (isPersistedRegisterField(key)) {
      persistDraftField(key, value);
    }
  };

  const toStepValues = <TStep extends RegisterStep>(
    stepToMap: TStep,
  ): RegisterStepValuesMap[TStep] => {
    const stepFields = getRegisterStepFields(stepToMap);
    return pickObject(values, stepFields);
  };

  const toStepFormState = <TStep extends RegisterStep>(
    stepToMap: TStep,
  ): RegisterStepFormState<TStep> => {
    switch (submission.type) {
      case 'sending-challenge':
      case 'verifying-code':
      case 'registering-account':
        return makeAsync.fetching();
      case 'error':
        return makeAsync.error(submission.message);
      case 'idle':
        return makeAsync.fetched(toStepValues(stepToMap));
    }
  };

  const clearCodeInput = useCallback(() => {
    autoSubmittedCodeRef.current = '';
    setValues((previousValues) => ({
      ...previousValues,
      code: '',
    }));
  }, []);

  const recoverWithSignInChallenge = useCallback(
    async (email: string): Promise<boolean> => {
      const recoveryPayload: RegisterSubmissionPayloadByStage['sending-challenge'] =
        {
          email,
          source: 'signin',
          mode: 'force',
        };
      setSubmission({
        type: 'sending-challenge',
        payload: recoveryPayload,
      });

      try {
        await model.dispatch('SendSignInChallenge', {
          email,
          options: { mode: 'force' },
        });
        clearCodeInput();
        setSessionSource('signin');
        setStep('code');
        setSubmission({ type: 'idle' });
        navigateWithAuthTransition(getPathForStep('code', 'signin'));
        return true;
      } catch {
        return false;
      }
    },
    [clearCodeInput, model],
  );

  const resendCode = useCallback(async (): Promise<void> => {
    if (!isEmailValid(values.email, validationRules)) {
      return;
    }

    const email = validationRules.parseEmail(values.email);
    if (!email) {
      return;
    }

    const challengePayload: RegisterSubmissionPayloadByStage['sending-challenge'] =
      {
        email,
        source: sessionSource,
        mode: 'force',
      };
    setSubmission({
      type: 'sending-challenge',
      payload: challengePayload,
    });

    try {
      if (sessionSource === 'signin') {
        await model.dispatch('SendSignInChallenge', {
          email,
          options: { mode: 'force' },
        });
      } else {
        await model.dispatch('SendRegistrationChallenge', {
          email,
          options: {
            mode: 'force',
          },
        });
      }
      setSubmission({ type: 'idle' });
      toast.success('Verification code sent successfully');
    } catch {
      setSubmission(
        toGenericSubmissionError('sending-challenge', challengePayload),
      );
    }
  }, [model, sessionSource, validationRules, values.email]);

  const submitCode = useCallback(async (): Promise<void> => {
    if (!canContinueFromStep('code', values, validationRules)) {
      return;
    }

    const email = validationRules.parseEmail(values.email);
    if (!email) {
      return;
    }

    const code = values.code.trim();
    const verificationPayload: RegisterSubmissionPayloadByStage['verifying-code'] =
      {
        email,
        code,
        source: sessionSource,
      };
    setSubmission({
      type: 'verifying-code',
      payload: verificationPayload,
    });

    try {
      const phone = validationRules.parsePhone(
        values.phoneCountryDialCode,
        values.phoneNumber,
      );
      const codeResult = await model.dispatch('SubmitRegistrationCode', {
        email,
        code,
        phone,
      });

      if (codeResult.type === 'existing-user') {
        if (sessionSource === 'signin') {
          trackSuccessfulLogin(codeResult.value);
          model.dispatch('LoginUser', codeResult.value);
          clearPersistedAndLocalFlowState();
          navigate(POST_AUTH_REDIRECT_PATH.login);
          return;
        }

        notifyExistingUserError({
          onSignIn: () => {
            clearPersistedAndLocalFlowState();
            navigateWithAuthTransition('/login' as const);
          },
        });
        clearCodeInput();
        setSubmission({
          type: 'error',
          message: EXISTING_USER_ERROR_MESSAGE,
          stage: 'verifying-code',
          payload: verificationPayload,
        });
        return;
      }

      const registrationValues = omitObject(values, ['code'] as const);
      const registrationPayload: RegisterSubmissionPayloadByStage['registering-account'] =
        {
          token: codeResult.value.token,
          values: registrationValues,
        };
      setSubmission({
        type: 'registering-account',
        payload: registrationPayload,
      });

      try {
        const registeredUser = await model.dispatch('CompleteRegistration', {
          token: codeResult.value.token,
          values: registrationValues,
        });

        trackSuccessfulLogin(registeredUser);
        emitGAEvent({
          type: 'signup_completed',
          payload: { creator_id: String(registeredUser.influencerId) },
        });
        model.dispatch('LoginUser', registeredUser);
        clearPersistedAndLocalFlowState();
        navigate(POST_AUTH_REDIRECT_PATH.registration);
      } catch (error) {
        if (isRecoverableCompleteRegistrationError(error)) {
          const wasRecovered = await recoverWithSignInChallenge(email);
          if (wasRecovered) {
            return;
          }
        }

        setSubmission(
          toGenericSubmissionError('registering-account', registrationPayload),
        );
      }
    } catch {
      notifyInvalidCodeError({
        onResend: () => {
          void resendCode();
        },
      });
      clearCodeInput();
      setSubmission({
        type: 'error',
        message: INVALID_CODE_ERROR_MESSAGE,
        stage: 'verifying-code',
        payload: verificationPayload,
      });
    }
  }, [
    clearPersistedAndLocalFlowState,
    clearCodeInput,
    model,
    recoverWithSignInChallenge,
    resendCode,
    sessionSource,
    validationRules,
    values,
  ]);

  useEffect(() => {
    if (step !== 'code') {
      autoSubmittedCodeRef.current = '';
      return;
    }

    const code = values.code.trim();
    if (code.length !== CONFIRMATION_CODE_LENGTH) {
      autoSubmittedCodeRef.current = '';
      return;
    }

    if (isSubmissionPending(submission)) {
      return;
    }

    if (autoSubmittedCodeRef.current === code) {
      return;
    }

    autoSubmittedCodeRef.current = code;
    void submitCode();
  }, [step, submission, submitCode, values.code]);

  const goToStep = (
    nextStep: RegisterStep,
    source: FlowSessionSource = 'signup',
  ): void => {
    setStep(nextStep);
    navigate(getPathForStep(nextStep, source));
  };

  const sendRegistrationChallengeFromPhoneStep = async (): Promise<void> => {
    const email = validationRules.parseEmail(values.email);
    if (!email) {
      return;
    }

    const ssoSession = useSsoSessionStore.getState();
    if (ssoSession.idToken && ssoSession.provider) {
      const registrationPayload: RegisterSubmissionPayloadByStage['registering-account'] =
        {
          token: ssoSession.idToken,
          values: omitObject(values, ['code'] as const),
        };
      setSubmission({
        type: 'registering-account',
        payload: registrationPayload,
      });

      try {
        const phone = validationRules.parsePhone(
          values.phoneCountryDialCode,
          values.phoneNumber,
        );
        const result = await ssoRegister({
          provider: ssoSession.provider,
          idToken: ssoSession.idToken,
          firstName: values.firstName?.trim() || undefined,
          lastName: values.lastName?.trim() || undefined,
          instagram:
            validationRules.parseInstagram(values.instagram) ?? undefined,
          tiktok: validationRules.parseTiktok(values.tiktok) ?? undefined,
          youtube:
            validationRules.parseYoutubeChannel(values.youtube) ?? undefined,
          mobilePhoneNumber: phone || undefined,
        });

        const user = result?.user ?? result;
        trackSuccessfulLogin(user);
        emitGAEvent({
          type: 'signup_completed',
          payload: { creator_id: String(user.influencerId) },
        });
        trackSignInWithSso(user, ssoSession.provider);
        useUserStore.getState().login(user);
        ssoSession.clearSsoSession();
        clearPersistedAndLocalFlowState();
        navigate(POST_AUTH_REDIRECT_PATH.registration);
      } catch {
        setSubmission(
          toGenericSubmissionError('registering-account', registrationPayload),
        );
      }
      return;
    }

    const challengePayload: RegisterSubmissionPayloadByStage['sending-challenge'] =
      {
        email,
        source: sessionSource,
        mode: 'if-needed',
      };
    trackMobilePhoneSubmitted(
      validationRules.parsePhone(
        values.phoneCountryDialCode,
        values.phoneNumber,
      ),
    );
    setSubmission({
      type: 'sending-challenge',
      payload: challengePayload,
    });

    try {
      await model.dispatch('SendRegistrationChallenge', { email });
      goToStep('code', sessionSource);
      setSubmission({ type: 'idle' });
    } catch {
      setSubmission(
        toGenericSubmissionError('sending-challenge', challengePayload),
      );
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (!canContinueFromStep(step, values, validationRules)) {
      return;
    }

    setSubmission({ type: 'idle' });
    trackContinueAction(
      step,
      toContinueTrackedValue(step, values, validationRules),
    );

    switch (step) {
      case 'email': {
        const email = validationRules.parseEmail(values.email);
        if (!email) {
          return;
        }

        model.dispatch('ClearEmailChallenge', { email });
        updateValue('email', email);
        goToStep('name');
        return;
      }

      case 'name':
        goToStep('instagram');
        return;

      case 'instagram':
        {
          const parsedInstagram = validationRules.parseInstagram(
            values.instagram,
          );
          const parsedTiktok = validationRules.parseTiktok(values.tiktok);
          const parsedYoutube = validationRules.parseYoutubeChannel(
            values.youtube,
          );
          if (!parsedInstagram && !parsedTiktok && !parsedYoutube) {
            return;
          }

          if (parsedInstagram) {
            updateValue('instagram', parsedInstagram);
          }
          if (parsedTiktok) {
            updateValue('tiktok', parsedTiktok);
          }
          if (parsedYoutube) {
            updateValue('youtube', parsedYoutube);
          }

          if (parsedInstagram) {
            track({ type: 'Instagram Submitted', payload: { uid: '' } });
          }
          track({
            type: 'Social Profiles Submitted',
            payload: {
              uid: '',
              instagram: parsedInstagram !== null,
              tiktok: parsedTiktok !== null,
              youtube: parsedYoutube !== null,
            },
          });

          void model
            .dispatch('SubmitHubspotRegistrationLead', {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              instagramHandle: parsedInstagram ?? '',
            })
            .catch(() => undefined);
        }
        goToStep('phone');
        return;

      case 'phone':
        await sendRegistrationChallengeFromPhoneStep();
        return;

      case 'code':
        await submitCode();
        return;
    }
  };

  const handleSkipPhone = async (): Promise<void> => {
    setSubmission({ type: 'idle' });
    await sendRegistrationChallengeFromPhoneStep();
  };

  const handleBack = (): void => {
    setSubmission({ type: 'idle' });

    if (sessionSource === 'signin' && step === 'code') {
      navigateWithAuthTransition(getPathForStep('email', 'signin'));
      return;
    }

    const previousStep = getPreviousRegisterStep(step);
    goToStep(previousStep);
  };

  const handleRestartFlow = (): void => {
    useSsoSessionStore.getState().clearSsoSession();
    clearPersistedAndLocalFlowState();
    navigate(getPathForStep('email', 'signup'));
  };

  const _dispatch = (action: RegisterAction): void => {
    switch (action.type) {
      case 'setField':
        if (!stepIncludesField(action.payload.step, action.payload.field)) {
          return;
        }
        updateValue(action.payload.field, action.payload.value);
        return;
      case 'continue':
        void handleContinue();
        return;
      case 'back':
        handleBack();
        return;
      case 'restartFlow':
        handleRestartFlow();
        return;
      case 'skipPhone':
        void handleSkipPhone();
        return;
      case 'submitCode':
        void submitCode();
        return;
      case 'resendCode':
        void resendCode();
        return;
      case 'googleSso':
        googleSso.initiateGoogleSignIn();
        return;
      case 'appleSso':
        void appleSso.initiateAppleSignIn();
        return;
    }
  };

  const dispatchers = getDispatchers<RegisterActionMap>(_dispatch);
  const canContinue = canContinueFromStep(step, values, validationRules);

  return toAsyncFetched({
    state: {
      step,
      values,
      submission,
      canContinue,
      googleSsoState: googleSso.state,
      appleSsoState: appleSso.state,
    },
    toStepFormState,
    ...dispatchers,
  });
};
